import React from 'react';
import TextInput from '../../components/TextInput';
import WheelDatePicker from '../../components/WheelDatePicker';
import ChoicePicker from '../../components/ChoicePicker';
import JpAddressInput from '../../components/JpAddressInput';
import { sendIcon } from '../../chatIcons';
import { normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import type { EmployeeSectionFlow } from './useEmployeeSectionFlow';
import type { HeadRelationship, YesNo } from './employeeSectionTypes';

const YES_NO = (t: EmployeeSectionFlow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.dependentDeduction.employee.yes') },
  { value: 'no', label: t('submitDocuments.dependentDeduction.employee.no') },
];

const HEAD_RELATIONSHIP_OPTIONS = (t: EmployeeSectionFlow['t']): { value: HeadRelationship; label: string }[] => [
  { value: 'spouse', label: t('submitDocuments.dependentDeduction.employee.headRelationship.spouse') },
  { value: 'child', label: t('submitDocuments.dependentDeduction.employee.headRelationship.child') },
  { value: 'mother', label: t('submitDocuments.dependentDeduction.employee.headRelationship.mother') },
  { value: 'father', label: t('submitDocuments.dependentDeduction.employee.headRelationship.father') },
  { value: 'grandparent', label: t('submitDocuments.dependentDeduction.employee.headRelationship.grandparent') },
  { value: 'sibling', label: t('submitDocuments.dependentDeduction.employee.headRelationship.sibling') },
];

export default function EmployeeSectionChatComposer({
  flow,
  wheelLocale,
}: {
  flow: EmployeeSectionFlow;
  wheelLocale?: string;
}) {
  const { step, t } = flow;

  if (step === 'fullName') {
    return (
      <TextInput
        fields={[
          {
            id: 'family',
            label: t('submitDocuments.profileName.field.family'),
            autoComplete: 'family-name',
            required: true,
            validate: flow.validateLegalName,
          },
          {
            id: 'first',
            label: t('submitDocuments.profileName.field.first'),
            autoComplete: 'given-name',
            required: true,
            validate: flow.validateLegalName,
          },
          {
            id: 'middle',
            label: t('submitDocuments.profileName.field.middle'),
            autoComplete: 'additional-name',
            validate: flow.validateLegalName,
          },
        ]}
        values={{
          family: flow.profile.familyName,
          first: flow.profile.firstName,
          middle: flow.profile.middleName,
        }}
        onChange={(id, value) => {
          flow.setProfile((p) => ({
            ...p,
            familyName: id === 'family' ? value : p.familyName,
            firstName: id === 'first' ? value : p.firstName,
            middleName: id === 'middle' ? value : p.middleName,
          }));
        }}
        onSubmit={() => void flow.handleSendFullName()}
        submitDisabled={!flow.canSendFullName}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'fullNameKana') {
    return (
      <TextInput
        fields={[
          {
            id: 'family',
            label: t('submitDocuments.profileName.field.kanaFamily'),
            required: true,
            validate: flow.validateKana,
          },
          {
            id: 'first',
            label: t('submitDocuments.profileName.field.kanaFirst'),
            required: true,
            validate: flow.validateKana,
          },
          {
            id: 'middle',
            label: t('submitDocuments.profileName.field.kanaMiddle'),
            validate: flow.validateKana,
          },
        ]}
        values={{
          family: flow.profile.familyNameKana,
          first: flow.profile.firstNameKana,
          middle: flow.profile.middleNameKana,
        }}
        onChange={(id, value) => {
          flow.setProfile((p) => ({
            ...p,
            familyNameKana: id === 'family' ? value : p.familyNameKana,
            firstNameKana: id === 'first' ? value : p.firstNameKana,
            middleNameKana: id === 'middle' ? value : p.middleNameKana,
          }));
        }}
        onSubmit={() => void flow.handleSendFullNameKana()}
        submitDisabled={!flow.canSendFullNameKana}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'dob') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={flow.dob} onChange={flow.setDob} locale={wheelLocale} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendDob()}
            aria-label={t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'myNumber') {
    return (
      <TextInput
        fields={[
          {
            id: 'myNumber',
            label: t('submitDocuments.profileName.field.myNumber'),
            type: 'tel',
            maxLength: 12,
            required: true,
            validate: flow.validateMyNumber,
          },
        ]}
        values={{ myNumber: flow.profile.myNumber }}
        onChange={(_id, value) =>
          flow.setProfile((p) => ({ ...p, myNumber: normalizeMyNumber(value).slice(0, 12) }))
        }
        onSubmit={() => void flow.handleSendMyNumber()}
        submitDisabled={!flow.canSendMyNumber}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'address') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <JpAddressInput
            value={flow.profile.address}
            onChange={(next) => flow.setProfile((p) => ({ ...p, address: next }))}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendAddress()}
            aria-label={t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'headOfHousehold') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.decl.isHeadOfHousehold || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handleHeadOfHousehold(v)}
        />
      </div>
    );
  }

  if (step === 'headName') {
    return (
      <TextInput
        fields={[
          {
            id: 'headName',
            label: t('submitDocuments.dependentDeduction.employee.headNameLabel'),
            required: true,
          },
        ]}
        values={{ headName: flow.decl.headOfHouseholdName }}
        onChange={(_id, value) => flow.setDecl((d) => ({ ...d, headOfHouseholdName: value }))}
        onSubmit={() => void flow.handleSendHeadName()}
        submitDisabled={!flow.canSendHeadName}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'headRelationship') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.decl.relationshipToHead || null}
          options={HEAD_RELATIONSHIP_OPTIONS(t)}
          onChange={(v) => void flow.handleHeadRelationship(v)}
        />
      </div>
    );
  }

  if (step === 'employeeEstimatedIncome') {
    return (
      <TextInput
        fields={[
          {
            id: 'income',
            label: t('submitDocuments.dependentDeduction.employee.employeeIncomeLabel'),
            required: true,
          },
        ]}
        values={{ income: flow.decl.employeeEstimatedIncome }}
        onChange={(_id, v) => flow.setDecl((d) => ({ ...d, employeeEstimatedIncome: v }))}
        onSubmit={() => void flow.handleEmployeeIncome()}
        submitDisabled={!flow.canSendEmployeeIncome}
        submitAriaLabel={t('submitDocuments.profileName.send')}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'multipleEmployers') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.decl.multipleEmployers || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handleMultipleEmployers(v)}
        />
      </div>
    );
  }

  if (step === 'multipleEmployersNotice') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleAcknowledgeNotice()}
            aria-label={t('submitDocuments.profileName.send')}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
